import type { HTMLAttributes } from 'react'
import clsx from 'clsx'

// ---------------------------------------------------------------------------
// Timeline
//
// Presentational vertical timeline. Props are domain-named; styling is
// delegated entirely to the .timeline / .tl-* family in
// shared/styles/components.css (token-backed, ported from the prototype).
//
// API (contracts/component-library.md):
//   items[] — { when, what, who?, tone? }
//     when  — timestamp / date string (already formatted by the caller)
//     what  — the event description (already i18n'd by the caller)
//     who   — optional actor label, rendered after a · separator
//     tone  — optional dot color: primary | success | warn | info | accent | danger
// ---------------------------------------------------------------------------

export type TimelineTone = 'primary' | 'success' | 'warn' | 'info' | 'accent' | 'danger'

export type TimelineItem = {
  /** Formatted timestamp or date string — no inline strings here. */
  when: string
  /** Event description (caller supplies already-localised text). */
  what: string
  /** Optional actor / source label. */
  who?: string
  /** Dot color tone. Omit for the default neutral dot. */
  tone?: TimelineTone
}

export type TimelineProps = {
  items: TimelineItem[]
} & Omit<HTMLAttributes<HTMLOListElement>, 'children'>

/**
 * Dotted vertical timeline. Presentational — no data fetching, no auth logic.
 * Safe for the public sessionless register bundle.
 *
 * Usage:
 *   <Timeline items={[
 *     { when: '07.05.2026 14:22', what: 'Application received', tone: 'primary' },
 *     { when: '08.05.2026 09:14', what: 'Assigned', who: 'Kari H.' },
 *   ]} />
 */
export function Timeline({ items, className, ...rest }: TimelineProps) {
  return (
    <ol className={clsx('timeline', className)} {...rest}>
      {items.map((item, index) => (
        <li
          // index is stable for static timeline lists; callers may pass a key
          // via the items array structure — using index is acceptable here as
          // timeline entries are typically ordered and not reordered in place.
          key={index}
          className="tl-item"
        >
          <div
            className={clsx('tl-dot', item.tone && `tl-dot--${item.tone}`)}
            aria-hidden="true"
          />
          <div>
            <div className="tl-when">{item.when}</div>
            <div className="tl-what">
              {item.what}
              {item.who !== undefined && (
                <>
                  {' · '}
                  <span className="tl-who">{item.who}</span>
                </>
              )}
            </div>
          </div>
        </li>
      ))}
    </ol>
  )
}
